// Config-driven finance/everyday calculator engine. Each variant declares its
// numeric input fields and a compute function returning labelled result lines.
// All math runs locally in the browser.

import { financePackMeta } from '@/lib/finance-pack';
import { financePack2Meta } from '@/lib/finance-pack-2';
import { mathPackMeta } from '@/lib/math-pack';
import { calcPack3Meta } from '@/lib/calc-pack-3';
import type { CalcPack3Slug } from '@/lib/calc-pack-3-slugs';
import type { MathPackSlug } from '@/lib/math-pack-slugs';
import type { FinancePackSlug } from '@/lib/finance-pack-slugs';

export type FinanceVariant =
  | 'percentage-calculator'
  | 'discount-calculator'
  | 'tip-calculator'
  | 'loan-calculator'
  | 'compound-interest-calculator'
  | 'sales-tax-calculator'
  | 'margin-calculator'
  | 'percentage-change-calculator'
  | 'percentage-increase-calculator'
  | 'percentage-difference-calculator'
  | 'gst-calculator'
  | 'fuel-cost-calculator'
  | 'electricity-cost-calculator'
  | 'grade-calculator'
  | 'roi-calculator'
  | 'mortgage-calculator'
  | 'hourly-to-salary-calculator'
  | 'investment-calculator'
  | 'retirement-calculator'
  | 'break-even-calculator'
  | 'debt-payoff-calculator'
  | 'bmi-calculator'
  | 'bmr-calculator'
  | 'tdee-calculator'
  | 'water-intake-calculator'
  | 'ideal-weight-calculator'
  | 'calorie-deficit-calculator'
  | 'macro-calculator'
  | 'cpm-calculator'
  | 'paypal-fee-calculator'
  | 'car-payment-calculator'
  | 'credit-card-payoff-calculator'
  | 'student-loan-calculator'
  | 'tax-calculator'
  | '401k-calculator'
  | 'house-affordability-calculator'
  | 'refinance-calculator'
  | 'paycheck-calculator'
  | 'calorie-calculator'
  | FinancePackSlug
  | MathPackSlug
  | CalcPack3Slug;

export type FinanceField = {
  key: string;
  label: string;
  placeholder: string;
  default: string;
  kind?: 'number' | 'select';
  options?: { label: string; value: string }[];
};

export type FinanceResultLine = { label: string; value: string };

export type FinanceMeta = {
  key: FinanceVariant;
  title: string;
  badge: string;
  fields: FinanceField[];
  compute: (values: Record<string, number>) => FinanceResultLine[];
  /** Footnote under the results; defaults to the currency-neutral finance note. */
  note?: string;
};

function money(value: number): string {
  if (!Number.isFinite(value)) return '0';
  return value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function plain(value: number, digits = 2): string {
  if (!Number.isFinite(value)) return '0';
  return value.toLocaleString('en-US', { maximumFractionDigits: digits });
}

export const financeMeta: Record<FinanceVariant, FinanceMeta> = {
  'percentage-calculator': {
    key: 'percentage-calculator',
    title: 'Percentage Calculator',
    badge: 'Percent',
    fields: [
      { key: 'percent', label: 'Percent (%)', placeholder: '15', default: '15' },
      { key: 'value', label: 'Of value', placeholder: '200', default: '200' },
      {
        key: 'part',
        label: 'Compare: what % is this of the value?',
        placeholder: '50',
        default: '50',
      },
    ],
    compute: ({ percent, value, part }) => {
      const result = (percent / 100) * value;
      return [
        { label: `${plain(percent)}% of ${plain(value)}`, value: plain(result) },
        {
          label: `${plain(part)} is what % of ${plain(value)}`,
          value: value === 0 ? '0%' : `${plain((part / value) * 100)}%`,
        },
        { label: `${plain(value)} plus ${plain(percent)}%`, value: plain(value + result) },
        { label: `${plain(value)} minus ${plain(percent)}%`, value: plain(value - result) },
      ];
    },
  },
  'discount-calculator': {
    key: 'discount-calculator',
    title: 'Discount Calculator',
    badge: 'Discount',
    fields: [
      { key: 'price', label: 'Original price', placeholder: '80', default: '80' },
      { key: 'discount', label: 'Discount (%)', placeholder: '25', default: '25' },
    ],
    compute: ({ price, discount }) => {
      const saved = (discount / 100) * price;
      const final = price - saved;
      return [
        { label: 'Final price', value: money(final) },
        { label: 'You save', value: money(saved) },
        { label: 'Discount', value: `${plain(discount)}%` },
      ];
    },
  },
  'tip-calculator': {
    key: 'tip-calculator',
    title: 'Tip Calculator',
    badge: 'Tip',
    fields: [
      { key: 'bill', label: 'Bill amount', placeholder: '50', default: '50' },
      { key: 'tip', label: 'Tip (%)', placeholder: '18', default: '18' },
      { key: 'people', label: 'Split between', placeholder: '2', default: '2' },
    ],
    compute: ({ bill, tip, people }) => {
      const tipAmount = (tip / 100) * bill;
      const total = bill + tipAmount;
      const perPerson = people > 0 ? total / people : total;
      return [
        { label: 'Tip amount', value: money(tipAmount) },
        { label: 'Total with tip', value: money(total) },
        { label: `Per person (${plain(people, 0)})`, value: money(perPerson) },
      ];
    },
  },
  'loan-calculator': {
    key: 'loan-calculator',
    title: 'Loan Calculator',
    badge: 'Loan',
    fields: [
      { key: 'amount', label: 'Loan amount', placeholder: '20000', default: '20000' },
      { key: 'rate', label: 'Annual interest rate (%)', placeholder: '6', default: '6' },
      { key: 'years', label: 'Term (years)', placeholder: '5', default: '5' },
    ],
    compute: ({ amount, rate, years }) => {
      const months = years * 12;
      const monthlyRate = rate / 100 / 12;
      let monthlyPayment: number;
      if (monthlyRate === 0) {
        monthlyPayment = months > 0 ? amount / months : 0;
      } else {
        monthlyPayment = (amount * monthlyRate) / (1 - Math.pow(1 + monthlyRate, -months));
      }
      const totalPaid = monthlyPayment * months;
      return [
        { label: 'Monthly payment', value: money(monthlyPayment) },
        { label: 'Total paid', value: money(totalPaid) },
        { label: 'Total interest', value: money(totalPaid - amount) },
      ];
    },
  },
  'compound-interest-calculator': {
    key: 'compound-interest-calculator',
    title: 'Compound Interest Calculator',
    badge: 'Interest',
    fields: [
      { key: 'principal', label: 'Principal', placeholder: '1000', default: '1000' },
      { key: 'rate', label: 'Annual rate (%)', placeholder: '5', default: '5' },
      { key: 'years', label: 'Years', placeholder: '10', default: '10' },
      { key: 'perYear', label: 'Compounds per year', placeholder: '12', default: '12' },
    ],
    compute: ({ principal, rate, years, perYear }) => {
      const n = perYear > 0 ? perYear : 1;
      const amount = principal * Math.pow(1 + rate / 100 / n, n * years);
      return [
        { label: 'Final amount', value: money(amount) },
        { label: 'Interest earned', value: money(amount - principal) },
        { label: 'Principal', value: money(principal) },
      ];
    },
  },
  'sales-tax-calculator': {
    key: 'sales-tax-calculator',
    title: 'Sales Tax Calculator',
    badge: 'Tax',
    fields: [
      { key: 'amount', label: 'Amount (before tax)', placeholder: '100', default: '100' },
      { key: 'rate', label: 'Sales tax rate (%)', placeholder: '8.5', default: '8.5' },
    ],
    compute: ({ amount, rate }) => {
      const tax = (rate / 100) * amount;
      return [
        { label: 'Sales tax', value: money(tax) },
        { label: 'Total with tax', value: money(amount + tax) },
      ];
    },
  },
  'margin-calculator': {
    key: 'margin-calculator',
    title: 'Margin Calculator',
    badge: 'Margin',
    fields: [
      { key: 'cost', label: 'Cost', placeholder: '40', default: '40' },
      { key: 'price', label: 'Selling price', placeholder: '100', default: '100' },
    ],
    compute: ({ cost, price }) => {
      const profit = price - cost;
      const margin = price > 0 ? (profit / price) * 100 : 0;
      const markup = cost > 0 ? (profit / cost) * 100 : 0;
      return [
        { label: 'Profit', value: money(profit) },
        { label: 'Margin', value: `${plain(margin)}%` },
        { label: 'Markup', value: `${plain(markup)}%` },
      ];
    },
  },
  'percentage-change-calculator': {
    key: 'percentage-change-calculator',
    title: 'Percentage Change Calculator',
    badge: 'Change',
    fields: [
      { key: 'from', label: 'From value', placeholder: '80', default: '80' },
      { key: 'to', label: 'To value', placeholder: '100', default: '100' },
    ],
    compute: ({ from, to }) => {
      const change = to - from;
      const percent = from !== 0 ? (change / Math.abs(from)) * 100 : 0;
      return [
        { label: 'Change', value: plain(change) },
        { label: 'Percentage change', value: `${plain(percent)}%` },
        {
          label: 'Direction',
          value: change > 0 ? 'Increase' : change < 0 ? 'Decrease' : 'No change',
        },
      ];
    },
  },
  'percentage-increase-calculator': {
    key: 'percentage-increase-calculator',
    title: 'Percentage Increase Calculator',
    badge: 'Increase',
    fields: [
      { key: 'from', label: 'Original value', placeholder: '80', default: '80' },
      { key: 'to', label: 'New value', placeholder: '100', default: '100' },
    ],
    compute: ({ from, to }) => {
      const change = to - from;
      const percent = from !== 0 ? (change / Math.abs(from)) * 100 : 0;
      return [
        { label: 'Increase', value: plain(change) },
        { label: 'Percentage increase', value: `${plain(percent)}%` },
        {
          label: 'Check',
          value:
            change >= 0
              ? `${plain(from)} grew by ${plain(Math.abs(percent))}% to ${plain(to)}`
              : `${plain(from)} fell by ${plain(Math.abs(percent))}%, which is a decrease`,
        },
      ];
    },
  },
  'percentage-difference-calculator': {
    key: 'percentage-difference-calculator',
    title: 'Percentage Difference Calculator',
    badge: 'Difference',
    fields: [
      { key: 'a', label: 'First value', placeholder: '80', default: '80' },
      { key: 'b', label: 'Second value', placeholder: '100', default: '100' },
    ],
    // Percentage DIFFERENCE is symmetric: |a-b| over the mean of the two, which
    // is what the term means in statistics and what the query intends. It is
    // deliberately NOT the percentage-change formula, which is directional.
    compute: ({ a, b }) => {
      const mean = (Math.abs(a) + Math.abs(b)) / 2;
      const diff = Math.abs(a - b);
      const percent = mean !== 0 ? (diff / mean) * 100 : 0;
      return [
        { label: 'Absolute difference', value: plain(diff) },
        { label: 'Percentage difference', value: `${plain(percent)}%` },
        {
          label: 'Note',
          value: 'Symmetric: the order of the two values does not matter',
        },
      ];
    },
  },
  'gst-calculator': {
    key: 'gst-calculator',
    title: 'GST Calculator',
    badge: 'GST',
    fields: [
      { key: 'amount', label: 'Amount (before GST)', placeholder: '100', default: '100' },
      { key: 'rate', label: 'GST rate (%)', placeholder: '10', default: '10' },
    ],
    compute: ({ amount, rate }) => {
      const gst = (rate / 100) * amount;
      return [
        { label: 'GST', value: money(gst) },
        { label: 'Total with GST', value: money(amount + gst) },
      ];
    },
  },
  'fuel-cost-calculator': {
    key: 'fuel-cost-calculator',
    title: 'Fuel Cost Calculator',
    badge: 'Fuel',
    fields: [
      { key: 'distance', label: 'Distance (km)', placeholder: '300', default: '300' },
      { key: 'efficiency', label: 'Consumption (L/100km)', placeholder: '7', default: '7' },
      { key: 'price', label: 'Price per liter', placeholder: '1.8', default: '1.8' },
    ],
    compute: ({ distance, efficiency, price }) => {
      const liters = (distance / 100) * efficiency;
      return [
        { label: 'Fuel needed (liters)', value: plain(liters, 2) },
        { label: 'Total fuel cost', value: money(liters * price) },
      ];
    },
  },
  'electricity-cost-calculator': {
    key: 'electricity-cost-calculator',
    title: 'Electricity Cost Calculator',
    badge: 'Power',
    fields: [
      { key: 'watts', label: 'Power (watts)', placeholder: '100', default: '100' },
      { key: 'hours', label: 'Hours per day', placeholder: '5', default: '5' },
      { key: 'price', label: 'Price per kWh', placeholder: '0.30', default: '0.30' },
    ],
    compute: ({ watts, hours, price }) => {
      const dailyKwh = (watts * hours) / 1000;
      const dailyCost = dailyKwh * price;
      return [
        { label: 'Daily energy (kWh)', value: plain(dailyKwh, 3) },
        { label: 'Daily cost', value: money(dailyCost) },
        { label: 'Monthly cost (30 days)', value: money(dailyCost * 30) },
      ];
    },
  },
  'grade-calculator': {
    key: 'grade-calculator',
    title: 'Grade Calculator',
    badge: 'Grade',
    fields: [
      { key: 'earned', label: 'Points earned', placeholder: '85', default: '85' },
      { key: 'total', label: 'Points possible', placeholder: '100', default: '100' },
    ],
    compute: ({ earned, total }) => {
      const percent = total > 0 ? (earned / total) * 100 : 0;
      let letter = 'F';
      if (percent >= 90) letter = 'A';
      else if (percent >= 80) letter = 'B';
      else if (percent >= 70) letter = 'C';
      else if (percent >= 60) letter = 'D';
      return [
        { label: 'Percentage', value: `${plain(percent, 1)}%` },
        { label: 'Letter grade', value: letter },
      ];
    },
  },
  'roi-calculator': {
    key: 'roi-calculator',
    title: 'ROI Calculator',
    badge: 'ROI',
    fields: [
      { key: 'invested', label: 'Amount invested', placeholder: '1000', default: '1000' },
      { key: 'returned', label: 'Amount returned', placeholder: '1500', default: '1500' },
    ],
    compute: ({ invested, returned }) => {
      const profit = returned - invested;
      const roi = invested !== 0 ? (profit / invested) * 100 : 0;
      return [
        { label: 'Net profit', value: money(profit) },
        { label: 'ROI', value: `${plain(roi, 2)}%` },
      ];
    },
  },
  'mortgage-calculator': {
    key: 'mortgage-calculator',
    title: 'Mortgage Calculator',
    badge: 'Mortgage',
    fields: [
      { key: 'amount', label: 'Loan amount', placeholder: '300000', default: '300000' },
      { key: 'rate', label: 'Annual interest rate (%)', placeholder: '5', default: '5' },
      { key: 'years', label: 'Term (years)', placeholder: '30', default: '30' },
    ],
    compute: ({ amount, rate, years }) => {
      const months = years * 12;
      const monthlyRate = rate / 100 / 12;
      const payment =
        monthlyRate === 0
          ? months > 0
            ? amount / months
            : 0
          : (amount * monthlyRate) / (1 - Math.pow(1 + monthlyRate, -months));
      const totalPaid = payment * months;
      return [
        { label: 'Monthly payment', value: money(payment) },
        { label: 'Total paid', value: money(totalPaid) },
        { label: 'Total interest', value: money(totalPaid - amount) },
      ];
    },
  },
  'hourly-to-salary-calculator': {
    key: 'hourly-to-salary-calculator',
    title: 'Hourly to Salary Calculator',
    badge: 'Salary',
    fields: [
      { key: 'rate', label: 'Hourly rate', placeholder: '25', default: '25' },
      { key: 'hours', label: 'Hours per week', placeholder: '40', default: '40' },
      { key: 'weeks', label: 'Weeks per year', placeholder: '52', default: '52' },
    ],
    compute: ({ rate, hours, weeks }) => {
      const annual = rate * hours * weeks;
      return [
        { label: 'Annual salary', value: money(annual) },
        { label: 'Monthly', value: money(annual / 12) },
        { label: 'Weekly', value: money(rate * hours) },
      ];
    },
  },
  'investment-calculator': {
    key: 'investment-calculator',
    title: 'Investment Calculator',
    badge: 'Invest',
    fields: [
      { key: 'principal', label: 'Starting amount', placeholder: '1000', default: '1000' },
      { key: 'monthly', label: 'Monthly contribution', placeholder: '200', default: '200' },
      { key: 'rate', label: 'Annual return (%)', placeholder: '7', default: '7' },
      { key: 'years', label: 'Years', placeholder: '20', default: '20' },
    ],
    compute: ({ principal, monthly, rate, years }) => {
      const r = rate / 100 / 12;
      const n = years * 12;
      const growth = r === 0 ? 1 : Math.pow(1 + r, n);
      const future = principal * growth + (r === 0 ? monthly * n : monthly * ((growth - 1) / r));
      const contributed = principal + monthly * n;
      return [
        { label: 'Future value', value: money(future) },
        { label: 'Total contributed', value: money(contributed) },
        { label: 'Interest earned', value: money(future - contributed) },
      ];
    },
  },
  'retirement-calculator': {
    key: 'retirement-calculator',
    title: 'Retirement Calculator',
    badge: 'Retire',
    fields: [
      { key: 'current', label: 'Current savings', placeholder: '20000', default: '20000' },
      { key: 'monthly', label: 'Monthly contribution', placeholder: '500', default: '500' },
      { key: 'rate', label: 'Annual return (%)', placeholder: '6', default: '6' },
      { key: 'years', label: 'Years until retirement', placeholder: '30', default: '30' },
    ],
    compute: ({ current, monthly, rate, years }) => {
      const r = rate / 100 / 12;
      const n = years * 12;
      const growth = r === 0 ? 1 : Math.pow(1 + r, n);
      const balance = current * growth + (r === 0 ? monthly * n : monthly * ((growth - 1) / r));
      return [
        { label: 'Projected balance', value: money(balance) },
        { label: 'Total contributed', value: money(current + monthly * n) },
      ];
    },
  },
  'break-even-calculator': {
    key: 'break-even-calculator',
    title: 'Break-Even Calculator',
    badge: 'Break-even',
    fields: [
      { key: 'fixed', label: 'Fixed costs', placeholder: '5000', default: '5000' },
      { key: 'price', label: 'Price per unit', placeholder: '40', default: '40' },
      { key: 'cost', label: 'Variable cost per unit', placeholder: '15', default: '15' },
    ],
    compute: ({ fixed, price, cost }) => {
      const margin = price - cost;
      const units = margin > 0 ? fixed / margin : 0;
      return [
        { label: 'Break-even units', value: margin > 0 ? plain(Math.ceil(units), 0) : 'n/a' },
        { label: 'Break-even revenue', value: margin > 0 ? money(units * price) : 'n/a' },
        { label: 'Contribution margin per unit', value: money(margin) },
      ];
    },
  },
  'debt-payoff-calculator': {
    key: 'debt-payoff-calculator',
    title: 'Debt Payoff Calculator',
    badge: 'Debt',
    fields: [
      { key: 'balance', label: 'Balance owed', placeholder: '8000', default: '8000' },
      { key: 'rate', label: 'Annual interest rate (%)', placeholder: '18', default: '18' },
      { key: 'payment', label: 'Monthly payment', placeholder: '300', default: '300' },
    ],
    compute: ({ balance, rate, payment }) => {
      const r = rate / 100 / 12;
      const interestFirst = balance * r;
      // A payment of zero, or one that only covers the interest, never clears the balance.
      // The old guard missed the zero-rate case and returned Infinity months.
      if (payment <= 0 || payment <= interestFirst) {
        return [
          { label: 'Months to pay off', value: 'Never at this payment' },
          { label: 'Payment must exceed', value: money(interestFirst) },
        ];
      }
      const months =
        r === 0 ? balance / payment : -Math.log(1 - (balance * r) / payment) / Math.log(1 + r);
      const totalPaid = payment * months;
      return [
        { label: 'Months to pay off', value: plain(Math.ceil(months), 0) },
        { label: 'Total paid', value: money(totalPaid) },
        { label: 'Total interest', value: money(totalPaid - balance) },
      ];
    },
  },
  'bmi-calculator': {
    key: 'bmi-calculator',
    title: 'BMI Calculator',
    badge: 'BMI',
    fields: [
      { key: 'weight', label: 'Weight (kg)', placeholder: '70', default: '70' },
      { key: 'height', label: 'Height (cm)', placeholder: '175', default: '175' },
    ],
    compute: ({ weight, height }) => {
      const meters = height / 100;
      const bmi = meters > 0 ? weight / (meters * meters) : 0;
      let category = 'Enter values';
      if (bmi > 0) {
        if (bmi < 18.5) category = 'Underweight';
        else if (bmi < 25) category = 'Normal weight';
        else if (bmi < 30) category = 'Overweight';
        else category = 'Obese';
      }
      return [
        { label: 'BMI', value: plain(bmi, 1) },
        { label: 'Category', value: category },
      ];
    },
  },
  'bmr-calculator': {
    key: 'bmr-calculator',
    title: 'BMR Calculator',
    badge: 'BMR',
    fields: [
      { key: 'weight', label: 'Weight (kg)', placeholder: '70', default: '70' },
      { key: 'height', label: 'Height (cm)', placeholder: '175', default: '175' },
      { key: 'age', label: 'Age (years)', placeholder: '30', default: '30' },
      {
        key: 'sex',
        label: 'Sex',
        placeholder: '',
        default: '1',
        kind: 'select',
        options: [
          { label: 'Male', value: '1' },
          { label: 'Female', value: '0' },
        ],
      },
    ],
    compute: ({ weight, height, age, sex }) => {
      const base = 10 * weight + 6.25 * height - 5 * age;
      const bmr = base + (sex === 1 ? 5 : -161);
      return [
        { label: 'BMR (calories/day)', value: plain(bmr, 0) },
        { label: 'This is your resting energy', value: 'at complete rest' },
      ];
    },
  },
  'tdee-calculator': {
    key: 'tdee-calculator',
    title: 'TDEE Calculator',
    badge: 'TDEE',
    fields: [
      { key: 'weight', label: 'Weight (kg)', placeholder: '70', default: '70' },
      { key: 'height', label: 'Height (cm)', placeholder: '175', default: '175' },
      { key: 'age', label: 'Age (years)', placeholder: '30', default: '30' },
      {
        key: 'sex',
        label: 'Sex',
        placeholder: '',
        default: '1',
        kind: 'select',
        options: [
          { label: 'Male', value: '1' },
          { label: 'Female', value: '0' },
        ],
      },
      {
        key: 'activity',
        label: 'Activity level',
        placeholder: '',
        default: '1.55',
        kind: 'select',
        options: [
          { label: 'Sedentary (little exercise)', value: '1.2' },
          { label: 'Light (1-3 days/week)', value: '1.375' },
          { label: 'Moderate (3-5 days/week)', value: '1.55' },
          { label: 'Active (6-7 days/week)', value: '1.725' },
          { label: 'Very active (hard training)', value: '1.9' },
        ],
      },
    ],
    compute: ({ weight, height, age, sex, activity }) => {
      const bmr = 10 * weight + 6.25 * height - 5 * age + (sex === 1 ? 5 : -161);
      const tdee = bmr * (activity || 1.2);
      return [
        { label: 'TDEE (calories/day)', value: plain(tdee, 0) },
        { label: 'BMR (at rest)', value: plain(bmr, 0) },
        { label: 'Mild weight loss (about -0.25 kg/wk)', value: plain(tdee - 275, 0) },
        { label: 'Mild weight gain (about +0.25 kg/wk)', value: plain(tdee + 275, 0) },
      ];
    },
  },
  'water-intake-calculator': {
    key: 'water-intake-calculator',
    title: 'Water Intake Calculator',
    badge: 'Water',
    fields: [{ key: 'weight', label: 'Weight (kg)', placeholder: '70', default: '70' }],
    compute: ({ weight }) => {
      const liters = weight * 0.033;
      return [
        { label: 'Daily water (liters)', value: plain(liters, 2) },
        { label: 'Daily water (ml)', value: plain(liters * 1000, 0) },
        { label: 'Cups (250 ml)', value: plain((liters * 1000) / 250, 1) },
      ];
    },
  },
  'ideal-weight-calculator': {
    key: 'ideal-weight-calculator',
    title: 'Ideal Weight Calculator',
    badge: 'Weight',
    fields: [
      { key: 'height', label: 'Height (cm)', placeholder: '175', default: '175' },
      {
        key: 'sex',
        label: 'Sex',
        placeholder: '',
        default: '1',
        kind: 'select',
        options: [
          { label: 'Male', value: '1' },
          { label: 'Female', value: '0' },
        ],
      },
    ],
    compute: ({ height, sex }) => {
      const overInches = Math.max(0, (height - 152.4) / 2.54);
      const devine = (sex === 1 ? 50 : 45.5) + 2.3 * overInches;
      return [
        { label: 'Ideal weight (Devine)', value: `${plain(devine, 1)} kg` },
        {
          label: 'Healthy BMI range',
          value: `${plain(18.5 * (height / 100) ** 2, 1)}–${plain(24.9 * (height / 100) ** 2, 1)} kg`,
        },
      ];
    },
  },
  'calorie-deficit-calculator': {
    key: 'calorie-deficit-calculator',
    title: 'Calorie Deficit Calculator',
    badge: 'Deficit',
    fields: [
      { key: 'maintenance', label: 'Maintenance calories', placeholder: '2200', default: '2200' },
      { key: 'deficit', label: 'Daily deficit', placeholder: '500', default: '500' },
    ],
    compute: ({ maintenance, deficit }) => {
      const target = maintenance - deficit;
      const weeklyLossKg = (deficit * 7) / 7700;
      const lines = [
        { label: 'Target daily calories', value: plain(Math.max(target, 0), 0) },
        { label: 'Estimated weekly loss', value: `${plain(weeklyLossKg, 2)} kg` },
      ];
      // A target at or below zero is not a diet, it is a broken input.
      if (target <= 0) {
        lines.push({
          label: 'Deficit too large',
          value: 'The deficit is larger than maintenance, so the target is not a real intake.',
        });
      }
      return lines;
    },
  },
  'macro-calculator': {
    key: 'macro-calculator',
    title: 'Macro Calculator',
    badge: 'Macros',
    fields: [
      { key: 'calories', label: 'Daily calories', placeholder: '2000', default: '2000' },
      { key: 'protein', label: 'Protein (%)', placeholder: '30', default: '30' },
      { key: 'carbs', label: 'Carbs (%)', placeholder: '40', default: '40' },
      { key: 'fat', label: 'Fat (%)', placeholder: '30', default: '30' },
    ],
    compute: ({ calories, protein, carbs, fat }) => {
      const split = protein + carbs + fat;
      const lines = [
        { label: 'Protein', value: `${plain((calories * (protein / 100)) / 4, 0)} g` },
        { label: 'Carbs', value: `${plain((calories * (carbs / 100)) / 4, 0)} g` },
        { label: 'Fat', value: `${plain((calories * (fat / 100)) / 9, 0)} g` },
      ];
      // The three shares have to describe one plate. 40/40/40 is not a split.
      if (Math.abs(split - 100) > 0.5) {
        lines.push({
          label: 'Check your split',
          value: `Protein, carbs and fat add up to ${plain(split, 0)}%, not 100%`,
        });
      }
      return lines;
    },
  },
  'cpm-calculator': {
    key: 'cpm-calculator',
    title: 'CPM Calculator',
    badge: 'Ads',
    fields: [
      { key: 'cost', label: 'Campaign cost', placeholder: '500', default: '500' },
      { key: 'impressions', label: 'Impressions', placeholder: '200000', default: '200000' },
    ],
    compute: ({ cost, impressions }) => {
      const cpm = impressions === 0 ? 0 : (cost / impressions) * 1000;
      return [
        { label: 'CPM (cost per 1,000)', value: money(cpm) },
        // A single impression costs fractions of a cent, so two decimal places round it to 0.00.
        {
          label: 'Cost per impression',
          value: plain(impressions === 0 ? 0 : cost / impressions, 5),
        },
      ];
    },
  },
  'paypal-fee-calculator': {
    key: 'paypal-fee-calculator',
    title: 'PayPal Fee Calculator',
    badge: 'Fees',
    fields: [
      { key: 'amount', label: 'Amount received', placeholder: '100', default: '100' },
      { key: 'percent', label: 'Fee percent (%)', placeholder: '2.9', default: '2.9' },
      { key: 'fixed', label: 'Fixed fee', placeholder: '0.30', default: '0.30' },
    ],
    compute: ({ amount, percent, fixed }) => {
      const fee = amount * (percent / 100) + fixed;
      return [
        { label: 'PayPal fee', value: money(fee) },
        { label: 'You receive', value: money(amount - fee) },
      ];
    },
  },
  'car-payment-calculator': {
    key: 'car-payment-calculator',
    title: 'Car Payment Calculator',
    badge: 'Auto',
    fields: [
      { key: 'price', label: 'Vehicle price', placeholder: '30000', default: '30000' },
      { key: 'down', label: 'Down payment', placeholder: '5000', default: '5000' },
      { key: 'rate', label: 'Interest rate (%)', placeholder: '6', default: '6' },
      { key: 'years', label: 'Loan term (years)', placeholder: '5', default: '5' },
    ],
    compute: ({ price, down, rate, years }) => {
      const principal = Math.max(price - down, 0);
      const months = years * 12;
      const monthlyRate = rate / 100 / 12;
      const monthly =
        monthlyRate === 0
          ? principal / (months || 1)
          : (principal * monthlyRate) / (1 - Math.pow(1 + monthlyRate, -months));
      return [
        { label: 'Monthly payment', value: money(monthly) },
        { label: 'Total paid', value: money(monthly * months) },
        { label: 'Total interest', value: money(monthly * months - principal) },
      ];
    },
  },
  'credit-card-payoff-calculator': {
    key: 'credit-card-payoff-calculator',
    title: 'Credit Card Payoff Calculator',
    badge: 'Debt',
    fields: [
      { key: 'balance', label: 'Card balance', placeholder: '4000', default: '4000' },
      { key: 'apr', label: 'APR (%)', placeholder: '19', default: '19' },
      { key: 'payment', label: 'Monthly payment', placeholder: '200', default: '200' },
    ],
    compute: ({ balance, apr, payment }) => {
      const monthlyRate = apr / 100 / 12;
      const minInterest = balance * monthlyRate;
      if (payment <= 0 || payment <= minInterest) {
        return [
          { label: 'Months to pay off', value: 'Never at this payment' },
          { label: 'Payment must exceed', value: money(minInterest) },
        ];
      }
      const months =
        monthlyRate === 0
          ? balance / payment
          : -Math.log(1 - (balance * monthlyRate) / payment) / Math.log(1 + monthlyRate);
      const total = payment * months;
      return [
        { label: 'Months to pay off', value: plain(Math.ceil(months), 0) },
        { label: 'Total paid', value: money(total) },
        { label: 'Total interest', value: money(total - balance) },
      ];
    },
  },
  'student-loan-calculator': {
    key: 'student-loan-calculator',
    title: 'Student Loan Calculator',
    badge: 'Loan',
    fields: [
      { key: 'amount', label: 'Loan amount', placeholder: '30000', default: '30000' },
      { key: 'rate', label: 'Interest rate (%)', placeholder: '5', default: '5' },
      { key: 'years', label: 'Repayment term (years)', placeholder: '10', default: '10' },
    ],
    compute: ({ amount, rate, years }) => {
      const months = years * 12;
      const monthlyRate = rate / 100 / 12;
      const monthly =
        monthlyRate === 0
          ? amount / (months || 1)
          : (amount * monthlyRate) / (1 - Math.pow(1 + monthlyRate, -months));
      return [
        { label: 'Monthly payment', value: money(monthly) },
        { label: 'Total paid', value: money(monthly * months) },
        { label: 'Total interest', value: money(monthly * months - amount) },
      ];
    },
  },
  'tax-calculator': {
    key: 'tax-calculator',
    title: 'Income Tax Calculator',
    badge: 'Tax',
    fields: [
      { key: 'income', label: 'Taxable income', placeholder: '60000', default: '60000' },
      { key: 'rate', label: 'Tax rate (%)', placeholder: '22', default: '22' },
    ],
    compute: ({ income, rate }) => {
      const tax = income * (rate / 100);
      return [
        { label: 'Estimated tax', value: money(tax) },
        { label: 'After-tax income', value: money(income - tax) },
        { label: 'Effective rate', value: `${plain(rate)}%` },
      ];
    },
  },
  '401k-calculator': {
    key: '401k-calculator',
    title: '401(k) Calculator',
    badge: 'Retirement',
    fields: [
      { key: 'balance', label: 'Current balance', placeholder: '20000', default: '20000' },
      {
        key: 'contribution',
        label: 'Annual contribution',
        placeholder: '10000',
        default: '10000',
      },
      { key: 'rate', label: 'Annual return (%)', placeholder: '7', default: '7' },
      { key: 'years', label: 'Years to grow', placeholder: '30', default: '30' },
    ],
    compute: ({ balance, contribution, rate, years }) => {
      const r = rate / 100;
      let total = balance;
      let contributed = balance;
      for (let i = 0; i < years; i += 1) {
        total = total * (1 + r) + contribution;
        contributed += contribution;
      }
      return [
        { label: 'Projected balance', value: money(total) },
        { label: 'Total contributed', value: money(contributed) },
        { label: 'Growth from returns', value: money(total - contributed) },
      ];
    },
  },
  'house-affordability-calculator': {
    key: 'house-affordability-calculator',
    title: 'House Affordability Calculator',
    badge: 'Home',
    fields: [
      { key: 'income', label: 'Annual income', placeholder: '90000', default: '90000' },
      { key: 'down', label: 'Down payment', placeholder: '40000', default: '40000' },
      { key: 'rate', label: 'Mortgage rate (%)', placeholder: '6.5', default: '6.5' },
      { key: 'years', label: 'Loan term (years)', placeholder: '30', default: '30' },
    ],
    compute: ({ income, down, rate, years }) => {
      // Use a 28% housing rule on gross monthly income to size the payment.
      const maxMonthly = (income / 12) * 0.28;
      const months = years * 12;
      const monthlyRate = rate / 100 / 12;
      const principal =
        monthlyRate === 0
          ? maxMonthly * months
          : (maxMonthly * (1 - Math.pow(1 + monthlyRate, -months))) / monthlyRate;
      return [
        { label: 'Estimated home price', value: money(principal + down) },
        { label: 'Estimated loan amount', value: money(principal) },
        { label: 'Target monthly payment', value: money(maxMonthly) },
      ];
    },
  },
  'refinance-calculator': {
    key: 'refinance-calculator',
    title: 'Refinance Calculator',
    badge: 'Refi',
    fields: [
      { key: 'balance', label: 'Loan balance', placeholder: '250000', default: '250000' },
      { key: 'current', label: 'Current rate (%)', placeholder: '7', default: '7' },
      { key: 'next', label: 'New rate (%)', placeholder: '5.5', default: '5.5' },
      { key: 'years', label: 'Remaining term (years)', placeholder: '25', default: '25' },
    ],
    compute: ({ balance, current, next, years }) => {
      const months = years * 12;
      const payment = (bal: number, ratePct: number): number => {
        const mr = ratePct / 100 / 12;
        return mr === 0 ? bal / months : (bal * mr) / (1 - Math.pow(1 + mr, -months));
      };
      const currentPayment = payment(balance, current);
      const newPayment = payment(balance, next);
      return [
        { label: 'Current monthly payment', value: money(currentPayment) },
        { label: 'New monthly payment', value: money(newPayment) },
        { label: 'Monthly savings', value: money(currentPayment - newPayment) },
      ];
    },
  },
  'paycheck-calculator': {
    key: 'paycheck-calculator',
    title: 'Paycheck Calculator',
    badge: 'Payroll',
    fields: [
      { key: 'gross', label: 'Gross pay per period', placeholder: '3000', default: '3000' },
      { key: 'taxRate', label: 'Tax withholding (%)', placeholder: '20', default: '20' },
      { key: 'deductions', label: 'Other deductions', placeholder: '200', default: '200' },
    ],
    compute: ({ gross, taxRate, deductions }) => {
      const tax = gross * (taxRate / 100);
      const net = gross - tax - deductions;
      return [
        { label: 'Take-home pay', value: money(net) },
        { label: 'Tax withheld', value: money(tax) },
        { label: 'Other deductions', value: money(deductions) },
      ];
    },
  },
  'calorie-calculator': {
    key: 'calorie-calculator',
    title: 'Calorie Calculator',
    badge: 'Calories',
    fields: [
      { key: 'weight', label: 'Weight (kg)', placeholder: '70', default: '70' },
      { key: 'height', label: 'Height (cm)', placeholder: '175', default: '175' },
      { key: 'age', label: 'Age (years)', placeholder: '30', default: '30' },
      {
        key: 'sex',
        label: 'Sex',
        placeholder: '1',
        default: '1',
        kind: 'select',
        options: [
          { label: 'Male', value: '1' },
          { label: 'Female', value: '0' },
        ],
      },
      {
        key: 'activity',
        label: 'Activity level',
        placeholder: '1.55',
        default: '1.55',
        kind: 'select',
        options: [
          { label: 'Sedentary', value: '1.2' },
          { label: 'Light', value: '1.375' },
          { label: 'Moderate', value: '1.55' },
          { label: 'Active', value: '1.725' },
          { label: 'Very active', value: '1.9' },
        ],
      },
    ],
    compute: ({ weight, height, age, sex, activity }) => {
      // Mifflin-St Jeor: BMR then multiply by activity for maintenance calories.
      const bmr = 10 * weight + 6.25 * height - 5 * age + (sex === 1 ? 5 : -161);
      const maintenance = bmr * activity;
      return [
        { label: 'Maintenance calories', value: `${plain(maintenance, 0)} kcal` },
        { label: 'Mild loss (0.25 kg/wk)', value: `${plain(maintenance - 275, 0)} kcal` },
        { label: 'Weight gain (0.25 kg/wk)', value: `${plain(maintenance + 275, 0)} kcal` },
      ];
    },
  },
  ...financePackMeta,
  ...financePack2Meta,
  ...mathPackMeta,
  ...calcPack3Meta,
};

export function getFinanceMeta(variant: FinanceVariant): FinanceMeta {
  return financeMeta[variant];
}

export function computeFinance(
  variant: FinanceVariant,
  raw: Record<string, string>,
): FinanceResultLine[] {
  const meta = financeMeta[variant];
  const values: Record<string, number> = {};
  for (const field of meta.fields) {
    const parsed = Number(raw[field.key]);
    values[field.key] = Number.isFinite(parsed) ? parsed : 0;
  }
  return meta.compute(values);
}
